import type { Kernel, Params } from "./kernel";
import type { RunRequest, RunResult } from "./runner";
import { makeRNG } from "./prng";
import { resultBits, kernelHash, resolveParams } from "./kernel";
import { seedFromString } from "./prng";
import { kernels } from "../kernels";
import { loadCustomKernel, hashSource, effectiveVerifiable } from "./custom-kernel";
import { currentTriple } from "./version";

// The deterministic compute core, factored out of the worker-pool plumbing so the
// determinism contract is testable without a browser. Both createLocalRunner (real Web
// Workers) and the headless test harness drive computation through the SAME plan here, so
// a passing test genuinely exercises the production reduction path.
//
// Determinism invariants (see src/sdk/CLAUDE.md):
//  1. chunk count = ceil(N / chunkSize), a function of (kernel, N) ONLY — never workers.
//  2. each chunk's RNG is seeded by (jobSeed, chunkIndex) ONLY — never by run order.
//  3. partials are reduced in fixed chunk-index order — never completion order.

export const DEFAULT_CHUNK_SIZE = 100_000;

export interface ChunkPlan {
  chunkSize: number;
  numChunks: number;
  /** Iterations in chunk `i` (the last chunk takes the remainder). */
  countFor(i: number): number;
}

/** The fixed chunk decomposition for a run. Depends on (kernel, N) only. */
export function planChunks(kernel: Kernel, n: number): ChunkPlan {
  const chunkSize = kernel.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const numChunks = Math.max(1, Math.ceil(n / chunkSize));
  return {
    chunkSize,
    numChunks,
    countFor: (i: number) => Math.min(chunkSize, n - i * chunkSize),
  };
}

/**
 * Compute one chunk deterministically: seed the RNG from (jobSeed, chunkIndex) and run the
 * kernel with the resolved params. This is exactly what the Web Worker does — kept here so
 * the worker, the local runner's fallback, and tests all share one definition. `params` must
 * already be resolved (defaults applied, clamped/snapped) — the result depends on it.
 */
export function computeChunk(
  kernel: Kernel,
  chunkIndex: number,
  count: number,
  jobSeed: number,
  math: import("./kernel").MathShim,
  params: Params,
): unknown {
  const rng = makeRNG(jobSeed, chunkIndex);
  return kernel.chunk(count, rng, math, params);
}

/**
 * Reduce partials in fixed chunk-index order. `partials[i]` must hold chunk i's output.
 * Float addition is order-sensitive, so this fixed order is part of the result identity.
 */
export function reduceOrdered(
  kernel: Kernel,
  partials: unknown[],
): { result: number; samples: number[] } {
  return kernel.reduce(partials);
}

/**
 * The per-chunk scalar estimate for a single partial — the live `ChunkEvent.sample` and the
 * datum that ends up in `samples[chunkIndex]` of the final reduce. Every gallery kernel
 * computes `samples[i]` purely from `partials[i]` (a self-contained per-chunk estimate), so
 * reducing the singleton `[partial]` yields exactly that chunk's sample. This is **advisory**:
 * it streams a preview live and never enters the canonical reduction, which still runs over
 * all partials in fixed chunk-index order. Falls back to the iteration count if a kernel's
 * reduce doesn't yield a usable scalar for a single chunk.
 */
export function chunkSample(kernel: Kernel, partial: unknown, count: number): number {
  try {
    const s = kernel.reduce([partial]).samples[0];
    return Number.isFinite(s as number) ? (s as number) : count;
  } catch {
    return count;
  }
}

// ── Shared run setup + result assembly ──────────────────────────────────────
// Both runners (local Web Worker pool, mesh WebSocket) resolve the job and assemble the
// RunResult through these, so they CANNOT produce different bits for the same inputs. The
// only thing a runner owns is *where* chunks are computed.

export interface ResolvedJob {
  kernel: Kernel;
  isCustom: boolean;
  jobSeed: number;
  params: Params;
  plan: ChunkPlan;
}

/** Resolve a RunRequest into the kernel, seed, params, and chunk plan. For custom kernels
 *  this loads + lints the source (throws on bad source) before any work is dispatched. */
export async function resolveJob(req: RunRequest): Promise<ResolvedJob> {
  let kernel: Kernel;
  let isCustom = false;
  if (req.kernelId === "custom") {
    if (!req.source) throw new Error("custom kernel run is missing source");
    kernel = await loadCustomKernel(req.source);
    isCustom = true;
  } else {
    const k = kernels[req.kernelId];
    if (!k) throw new Error(`Unknown kernel: ${req.kernelId}`);
    kernel = k;
  }
  return {
    kernel,
    isCustom,
    jobSeed: seedFromString(req.seed),
    params: resolveParams(kernel.params, req.params),
    plan: planChunks(kernel, req.n),
  };
}

/** Assemble the final RunResult from ordered partials. Identical for local and mesh. */
export function assembleResult(
  req: RunRequest,
  job: ResolvedJob,
  partials: unknown[],
  elapsedMs: number,
): RunResult {
  const { result, samples } = reduceOrdered(job.kernel, partials);
  const verifiable = job.isCustom
    ? effectiveVerifiable(!!job.kernel.verifiable, req.source!)
    : job.kernel.verifiable;
  return {
    result,
    resultBits: resultBits(result),
    samples,
    kernelHash: job.isCustom ? hashSource(req.source!) : kernelHash(job.kernel),
    kernelId: req.kernelId,
    resultUnit: job.kernel.resultUnit ?? "result",
    verifiable,
    seed: req.seed,
    n: req.n,
    params: job.params,
    elapsedMs,
    triple: currentTriple(),
  };
}
